import { useEffect, useState, useCallback } from "react";
import { store as accBootStore } from "../../components/Lit/acc-boot/acc-boot.js";

export interface LosStoreState {
  loading: boolean;
  inTreeView: boolean;
  inInternational: boolean;
  hideInternationalDownlines: boolean;
  hideVolume: boolean;
  hideABOsZeroVolume: boolean;
  rwlosSearchData: LosSearchRecord[] | null;
  expandedAbos: string[];
}

export interface LosSearchRecord {
  affAbo: string;
  abo?: number;
  aboNumber?: string;
  profile: { name: string; localName?: string; country?: string; preferredLanguageCode?: string };
  flags?: { isInternational?: boolean; isPrivate?: boolean };
  inode?: { children?: LosSearchRecord[] };
}

const DEFAULT_STATE: LosStoreState = {
  loading: true,
  inTreeView: false,
  inInternational: false,
  hideInternationalDownlines: true,
  hideVolume: false,
  hideABOsZeroVolume: false,
  rwlosSearchData: null,
  expandedAbos: [],
};

// acc-boot's createStore hands every subscriber the *entire* store state rather than
// the patch that was just written, so a write to any unrelated slice (user, tokens,
// locale, bonusPeriod, ...) notifies us too. Narrowing to the keys we actually read,
// and bailing out when none of them changed, is what keeps this hook from
// re-rendering its consumers on every unrelated store write
const TRACKED_KEYS = Object.keys(DEFAULT_STATE) as (keyof LosStoreState)[];

export const useAccBootStore = () => {
  // Seed from the store's current value (not just DEFAULT_STATE) so a fresh mount -
  // e.g. TreeView remounting after a filter changed while it was unmounted - reflects
  // what's already in the store instead of waiting for the next setState to fire.
  const [state, setState] = useState<LosStoreState>(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const current = (accBootStore as any).getState() as Partial<LosStoreState>;
    const seeded = { ...DEFAULT_STATE };
    for (const key of TRACKED_KEYS) {
      const value = current[key];
      if (value !== undefined) (seeded as Record<string, unknown>)[key] = value;
    }
    return seeded;
  });

  useEffect(() => {
    let isActive = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const unsubscribe = (accBootStore as any).subscribe((s: Partial<LosStoreState>) => {
      queueMicrotask(() => {
        if (!isActive) return;
        setState((prev) => {
          const patch: Partial<LosStoreState> = {};
          let changed = false;
          for (const key of TRACKED_KEYS) {
            const value = s[key];
            // Skip keys the store hasn't populated yet so they keep their default
            // instead of being clobbered with undefined.
            if (value === undefined || value === prev[key]) continue;
            (patch as Record<string, unknown>)[key] = value;
            changed = true;
          }
          // Returning prev by identity is the important part. The Lit grid writes
          // `loading` from inside its own render pass (renderDownlineSection in
          // accWcViewLosIndented), so an always-new merged object here turns every
          // Lit repaint into a React re-render - and once a second grid is
          // subscribed (EdLOS "View Downline LOS" mounts another acc-wc-los-ar,
          // the same component RwLos uses) that becomes an unbounded update loop.
          return changed ? { ...prev, ...patch } : prev;
        });
      });
    }) as (() => void) | void;

    return () => {
      isActive = false;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);

  const setStoreState = useCallback((patch: Partial<LosStoreState>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (accBootStore as any).setState(patch);
  }, []);

  return { state, setStoreState };
};
